import { Injectable } from "@angular/core";
import { ActivatedRoute, Router, UrlSegment } from "@angular/router";
import { HttpClient } from "@angular/common/http";
import { SettingsService } from "./settings.service";
import { catchError, map, switchMap, take } from "rxjs/operators";
import { EMPTY, of } from "rxjs";

type Source = "whatsapp" | "clinic_site" | "direct";

@Injectable({ providedIn: "root" })
export class AnalyticsService {
  constructor(private http: HttpClient, private settingsService: SettingsService) {}

  handleEntry(route: ActivatedRoute, router: Router, onCanonicalReady: (key: string) => void): void {
    route.url.subscribe((segments: UrlSegment[]) => {
      const first = segments[0]?.path; // 'w' | 'c' | '<key>'
      const second = segments[1]?.path; // <key> if prefixed
      const isPrefixed = first === "w" || first === "c";
      const key = isPrefixed ? second : first;
      if (!key) return;

      const src: Source = first === "w" ? "whatsapp" : first === "c" ? "clinic_site" : "direct";

      // console.log("[Attribution] entry detected:", {
      //   pathSegments: segments.map((s) => s.path),
      //   isPrefixed,
      //   resolvedKey: key,
      //   source: src,
      // });

      if (isPrefixed) {
        // first_source — only once and only for real channels
        if (!localStorage.getItem("first_source") && src !== "direct") {
          const firstSource = { source: src, key, captured_at: new Date().toISOString() };
          localStorage.setItem("first_source", JSON.stringify(firstSource));
          // console.log("[Attribution] Stored first_source:", firstSource);
        }

        // last_source — write for this channel entry
        const lastSource = { source: src, key, captured_at: new Date().toISOString() };
        localStorage.setItem("last_source", JSON.stringify(lastSource));
        // console.log("[Attribution] Stored last_source:", lastSource);

        // Carry the true source across the redirect
        sessionStorage.setItem("attr_redirect_ts", String(Date.now()));
        sessionStorage.setItem("pending_src", src);

        // console.log("[Attribution] Cleaning URL → navigating to canonical path:", `/${key}`);
        router.navigate(["/", key], { replaceUrl: true });
        return; // canonical pass will fire next
      }

      // Canonical pass (/:key)
      const ts = Number(sessionStorage.getItem("attr_redirect_ts") || 0);
      const recentlyRedirected = ts && Date.now() - ts < 3000; // 3s window

      // Decide the effective source for this canonical hit
      let effectiveSrc: Source = "direct";
      if (recentlyRedirected) {
        const pending = (sessionStorage.getItem("pending_src") as Source | null) || null;
        if (pending && pending !== "direct") {
          effectiveSrc = pending;
        }
      }

      // Only store "direct" if it wasn't an immediate redirect from a channel
      if (!(recentlyRedirected && effectiveSrc !== "direct")) {
        const lastSource = { source: effectiveSrc, key, captured_at: new Date().toISOString() };
        localStorage.setItem("last_source", JSON.stringify(lastSource));
        // console.log("[Attribution] Stored last_source:", lastSource);
      } else {
        // console.log(
        //   '[Attribution] Suppressing "direct" overwrite after recent redirect; using channel:',
        //   effectiveSrc
        // );
      }

      // Cleanup temp flags
      if (recentlyRedirected) {
        sessionStorage.removeItem("attr_redirect_ts");
        sessionStorage.removeItem("pending_src");
      }

      // hand control back to the component once we’re on the canonical route
      onCanonicalReady(key);

      // IMPORTANT: log using the effective source (not "(direct)" after redirect)
      this.logEntryToServer(key, effectiveSrc);
    });

    // Fallback: if only params change on canonical route (rare), still invoke callback
    route.params.subscribe((params) => {
      const key = params["key"];
      if (key) onCanonicalReady(key);
    });
  }

  private logEntryToServer(key: string, src: Source) {
    this.settingsService
      .getClinic(key)
      .pipe(
        take(1),
        map((data: any) => data?.GID ?? null),
        switchMap((GID) => {
          const payload = {
            GID,
            Source: src,
            UserAgent: navigator.userAgent,
          };
          return this.http.post<any>("api/settings/analytics", payload);
        })
      )
      .subscribe({
        next: (resp) => {
          if (resp?.AutoIndex) {
            // store the apptVisitSource AutoIndex so we can update attatch more details PID, AppointmentId etc.
            sessionStorage.setItem("visit_id", String(resp.AutoIndex));
            // console.log("[Attribution] Visit ID stored:", resp.AutoIndex);
          }
        },
        error: (err) => console.error("[Attribution] Failed to log entry:", err),
      });
  }

  updateVisit(fields: {
    PID?: string | number;
    AppointmentId?: string | number;
    ID?: string | number;
    patMsgId?: string | number;
    patAbandonedId?: string | number;
    apptActivityId?: string | number;
  }) {
    const id = Number(sessionStorage.getItem("visit_id") || 0);
    if (!id) return of(null);

    return this.http.put(`api/settings/analytics/${id}`, fields);
  }
}
